/**
 * TeammateSelector — searchable Headless UI Combobox for picking a
 * teammate, or (optionally) one of a set of "extras" rendered alongside
 * the teammates list.
 *
 * Two consumers today:
 *   - TransferFundsModal — no extras. User picks a teammate to send funds to.
 *   - DashboardPage (Phase D.4) — extras include "My Stats", "Team Summary",
 *     "Team + My Stats" to switch the dashboard's scope.
 *
 * Stays fully controlled — parent owns the teammates list, the selected
 * teammate, optionally the selected extra id, the "adjust" callback, and
 * decides what to render next to it.
 *
 * Selection routing: a single Combobox underneath, but extras and
 * teammates are tracked via SEPARATE parent state. Picking a teammate
 * fires onChange(teammate). Picking an extra fires onExtraSelect(id).
 * Parents typically clear the other side when handling either callback.
 *
 * Behaviors baked in:
 *  - tab focusable (the input IS the trigger)
 *  - type to filter (case-insensitive match on display_name OR
 *    location_id for teammates; label for extras)
 *  - alphabetical sort by display_name for teammates (extras stay in the
 *    order provided)
 *  - search-magnifier icon fades in only when input is focused
 *  - on-focus select-all so user can replace selection by typing
 *  - sticky "Adjust Team Members" footer that closes the Combobox via a
 *    synthetic Escape keydown (the only mechanism that reliably closes
 *    the panel in our stacked-modal setup — see AdjustFooter comment)
 *  - 44px min row height for touch targets
 *  - panel anchored absolutely under the input (no portal — survives
 *    inside scrolling modal containers)
 */

import { useState, type ReactNode } from 'react';
import { ChevronDown, Search, Check, Settings } from 'lucide-react';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';

export interface Teammate {
  id: string;
  location_id: string;
  display_name: string;
}

/**
 * Extra item rendered alongside teammates in the dropdown. Used by the
 * dashboard scope selector to add "My Stats", "Team Summary", "Team +
 * My Stats" above and below the teammates list.
 */
export interface ExtraItem {
  /** Stable id passed back via onExtraSelect and used to compare in selectedExtraId. */
  id: string;
  /** Text shown in the option, in the trigger when selected, and matched against search. */
  label: string;
  /** Optional icon shown left of the label. */
  icon?: ReactNode;
  /** Where to render the item: 'top' = before teammates, 'bottom' = after. */
  position: 'top' | 'bottom';
  /** If true, renders a thin divider above this item. */
  separatorBefore?: boolean;
}

/**
 * Alphabetical sort by display_name (case-insensitive), falling back to
 * location_id when display_name is empty. Exported so consumers that need
 * a sorted list outside the selector (e.g. a "Remove Teammate" list) get
 * the same ordering.
 */
export function sortTeammates(teammates: Teammate[]): Teammate[] {
  return teammates.slice().sort((a, b) =>
    (a.display_name || a.location_id).localeCompare(
      b.display_name || b.location_id,
      undefined,
      { sensitivity: 'base' }
    )
  );
}

interface TeammateSelectorProps {
  value: Teammate | null;
  onChange: (t: Teammate | null) => void;
  teammates: Teammate[];
  onAdjust: () => void;
  disabled?: boolean;
  placeholder?: string;
  emptyText?: string;
  /** Optional special items (e.g. "My Stats") rendered alongside teammates. */
  extras?: ExtraItem[];
  /** Id of the currently-selected extra. Mutually exclusive with `value`. */
  selectedExtraId?: string | null;
  /** Fires when the user picks an extra item from the dropdown. */
  onExtraSelect?: (id: string) => void;
}

/**
 * Internal footer button. Closes the Combobox panel by dispatching a
 * synthetic Escape keydown from the currently-focused element — Headless
 * UI listens for this as its documented keyboard close trigger, and
 * bubbling from activeElement reaches its handlers wherever they're
 * attached.
 *
 * We tried two earlier approaches that both failed in our stacked-modal
 * setup:
 *   - comboboxButtonRef.current?.click() — toggling via the trigger
 *     button. Worked initially but stopped after sibling components
 *     started mounting in the same render cycle (D.2).
 *   - useClose() hook — HUI's documented programmatic close. Did not
 *     close the panel even with stopPropagation.
 *
 * Escape dispatch is the third try and the one that holds. stopPropagation
 * prevents the inside-panel click from triggering any "keep open" handler.
 */
function AdjustFooter({ onAdjust }: { onAdjust: () => void }) {
  return (
    <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          const target = (document.activeElement as HTMLElement) || document;
          target.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: 'Escape',
              code: 'Escape',
              bubbles: true,
              cancelable: true,
            })
          );
          onAdjust();
        }}
        className="w-full flex items-center gap-2 px-3 py-3 text-sm text-gray-700 hover:bg-gray-50 text-left"
      >
        <Settings className="w-4 h-4 flex-shrink-0" />
        <span>Adjust Team Members</span>
      </button>
    </div>
  );
}

/**
 * Renders an extra item as a ComboboxOption. Extracted because extras
 * render in two places (top and bottom of the list).
 */
function ExtraOption({ extra }: { extra: ExtraItem }) {
  return (
    <>
      {extra.separatorBefore && <div className="border-t border-gray-200" />}
      <ComboboxOption value={extra.id} className="cursor-pointer">
        {({ focus, selected }) => (
          <div
            className={`flex items-center justify-between gap-2 px-3 min-h-[44px] text-sm ${
              focus ? 'bg-purple-50 text-purple-900' : 'text-gray-900'
            }`}
          >
            <span className={`flex items-center gap-2 truncate ${selected ? 'font-medium' : ''}`}>
              {extra.icon}
              {extra.label}
            </span>
            {selected && <Check className="w-4 h-4 text-purple-600 flex-shrink-0" />}
          </div>
        )}
      </ComboboxOption>
    </>
  );
}

export function TeammateSelector({
  value,
  onChange,
  teammates,
  onAdjust,
  disabled = false,
  placeholder,
  emptyText = 'No teammates added yet',
  extras = [],
  selectedExtraId = null,
  onExtraSelect,
}: TeammateSelectorProps) {
  const [query, setQuery] = useState('');

  // Single string id used as the Combobox value: either an extra's id
  // (when selectedExtraId is set) or a teammate's location_id (when value
  // is set). Extras take precedence so the dropdown reflects the parent's
  // selectedExtraId even if `value` is also non-null.
  const internalValue: string | null =
    selectedExtraId ?? value?.location_id ?? null;

  const topExtras = extras.filter(e => e.position === 'top');
  const bottomExtras = extras.filter(e => e.position === 'bottom');
  const sortedTeammates = sortTeammates(teammates);

  // Filter applies to both teammates and extras (case-insensitive).
  // Extras match by label; teammates by display_name or location_id.
  const q = query.trim().toLowerCase();
  const matchExtra = (e: ExtraItem) => q === '' || e.label.toLowerCase().includes(q);
  const matchTeammate = (t: Teammate) =>
    q === '' ||
    (t.display_name || '').toLowerCase().includes(q) ||
    t.location_id.toLowerCase().includes(q);

  const filteredTopExtras = topExtras.filter(matchExtra);
  const filteredTeammates = sortedTeammates.filter(matchTeammate);
  const filteredBottomExtras = bottomExtras.filter(matchExtra);

  const totalMatches =
    filteredTopExtras.length + filteredTeammates.length + filteredBottomExtras.length;

  const effectivePlaceholder =
    placeholder ?? (teammates.length === 0 && extras.length === 0
      ? emptyText
      : 'Select a teammate...');

  // Resolve internalValue → label for the input display.
  const displayValueFor = (id: string | null): string => {
    if (!id) return '';
    const extra = extras.find(e => e.id === id);
    if (extra) return extra.label;
    const teammate = teammates.find(t => t.location_id === id);
    if (teammate) return teammate.display_name || teammate.location_id;
    return '';
  };

  // Route the Combobox onChange to the right parent callback.
  const handleSelect = (id: string | null) => {
    setQuery('');
    if (!id) {
      onChange(null);
      return;
    }
    const extra = extras.find(e => e.id === id);
    if (extra) {
      onExtraSelect?.(id);
      return;
    }
    const teammate = teammates.find(t => t.location_id === id);
    if (teammate) {
      onChange(teammate);
    }
  };

  return (
    <Combobox
      value={internalValue}
      onChange={handleSelect}
      onClose={() => setQuery('')}
      disabled={disabled}
    >
      <div className="relative">
        <ComboboxInput
          displayValue={displayValueFor}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={(e) => e.target.select()}
          placeholder={effectivePlaceholder}
          className="peer block w-full pl-3 pr-10 py-2.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:pl-9 transition-[padding] data-[disabled]:bg-gray-100 data-[disabled]:cursor-not-allowed"
        />
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 opacity-0 peer-focus:opacity-100 transition-opacity" />
        <ComboboxButton
          className="absolute inset-y-0 right-0 px-3 flex items-center focus:outline-none"
        >
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </ComboboxButton>
        <ComboboxOptions className="absolute left-0 right-0 mt-1 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg focus:outline-none z-[60]">
          {totalMatches === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">
              {teammates.length === 0 && extras.length === 0 ? emptyText : 'No matches'}
            </div>
          ) : (
            <>
              {filteredTopExtras.map((extra) => (
                <ExtraOption key={extra.id} extra={extra} />
              ))}
              {filteredTopExtras.length > 0 && filteredTeammates.length > 0 && (
                <div className="border-t border-gray-200" />
              )}
              {filteredTeammates.map((teammate) => (
                <ComboboxOption
                  key={teammate.location_id}
                  value={teammate.location_id}
                  className="cursor-pointer"
                >
                  {({ focus, selected }) => (
                    <div
                      className={`flex items-center justify-between gap-2 px-3 min-h-[44px] text-sm ${
                        focus ? 'bg-purple-50 text-purple-900' : 'text-gray-900'
                      }`}
                    >
                      <span className={`truncate ${selected ? 'font-medium' : ''}`}>
                        {teammate.display_name || teammate.location_id}
                      </span>
                      {selected && <Check className="w-4 h-4 text-purple-600 flex-shrink-0" />}
                    </div>
                  )}
                </ComboboxOption>
              ))}
              {filteredBottomExtras.length > 0 && filteredTeammates.length > 0 && (
                <div className="border-t border-gray-200" />
              )}
              {filteredBottomExtras.map((extra) => (
                <ExtraOption key={extra.id} extra={extra} />
              ))}
            </>
          )}

          {/* Footer: Adjust Team Members (sticky at bottom of scroll) */}
          <AdjustFooter onAdjust={onAdjust} />
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}
